import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query"
import {
	ErrorResponse,
	InfinitivePage,
	SuccessCursorResponse,
	SuccessResponse,
	useHttpClient,
} from "api/api"
import {
	Notification,
	NotificationFeedStatus,
	NotificationPreferences,
	NotificationUnreadCount,
	UpdateNotificationPreferencesPayload,
} from "interface/notification"
import { useInvalidateOnRealtimeEvent } from "modules/realtime/useInvalidateOnRealtimeEvent"
import { useMemo } from "react"

export const queryKeys = {
	notifications: "notifications",
	notificationsUnreadCount: "notifications-unread-count",
	notificationsReadAll: "notifications-read-all",
	notificationsRead: "notifications-read",
	notificationPreferences: "notification-preferences",
	updateNotificationPreferences: "update-notification-preferences",
}

const flattenPages = ({
	pages,
}: {
	pages: InfinitivePage<Notification>[]
}) => ({
	pages: pages.flatMap(({ data }) => data),
})

export const useNotifications = (status: NotificationFeedStatus) => {
	const { httpClient } = useHttpClient()
	const queryKey = useMemo(() => [queryKeys.notifications, status], [status])

	useInvalidateOnRealtimeEvent(
		["notification_unread_count_update", "escalation_updated"],
		{
			queryKey: [queryKeys.notifications],
		},
	)

	return useInfiniteQuery<
		InfinitivePage<Notification>,
		Error,
		{ pages: Notification[] }
	>({
		queryKey,
		queryFn: async ({ pageParam, signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessCursorResponse<Notification>>(
				"/api/v1/notifications",
				{
					signal,
					params: {
						status,
						cursor: pageParam,
					},
				},
			)

			return {
				data: data.items,
				nextCursor: data.nextCursor,
			}
		},
		initialPageParam: undefined,
		placeholderData: keepPreviousData,
		select: flattenPages,
		getNextPageParam: (lastPage) => lastPage.nextCursor,
	})
}

export const useNotificationsUnreadCount = (enabled = true) => {
	const { httpClient } = useHttpClient()
	const queryKey = useMemo(() => [queryKeys.notificationsUnreadCount], [])

	useInvalidateOnRealtimeEvent("notification_unread_count_update", {
		queryKey,
		enabled,
	})

	return useQuery({
		enabled,
		queryKey,
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<NotificationUnreadCount>>(
				"/api/v1/notifications/unread-count",
				{ signal },
			)
			return data
		},
	})
}

export const useReadAllNotifications = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<NotificationUnreadCount, ErrorResponse>({
		mutationKey: [queryKeys.notificationsReadAll],
		mutationFn: async () => {
			const {
				data: { data },
			} = await httpClient.patch<SuccessResponse<NotificationUnreadCount>>(
				"/api/v1/notifications/read-all",
			)
			return data
		},
		onSuccess: (data) => {
			queryClient.setQueryData([queryKeys.notificationsUnreadCount], data)
			queryClient.invalidateQueries({
				queryKey: [queryKeys.notifications],
			})
		},
	})
}

export const useReadNotification = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()
	type ReadNotificationPayload = Pick<Notification, "id">

	return useMutation<Notification, ErrorResponse, ReadNotificationPayload>({
		mutationKey: [queryKeys.notificationsRead],
		mutationFn: async ({ id }: ReadNotificationPayload) => {
			const {
				data: { data },
			} = await httpClient.patch<SuccessResponse<Notification>>(
				`/api/v1/notifications/${encodeURIComponent(id)}/read`,
			)
			return data
		},
		onSuccess: () => {
			queryClient.setQueryData<NotificationUnreadCount>(
				[queryKeys.notificationsUnreadCount],
				(prev) =>
					prev ? { unreadCount: Math.max(0, prev.unreadCount - 1) } : prev,
			)
			queryClient.invalidateQueries({
				queryKey: [queryKeys.notifications],
			})
		},
	})
}

export const useNotificationPreferences = () => {
	const { httpClient } = useHttpClient()
	const queryKey = useMemo(() => [queryKeys.notificationPreferences], [])

	return useQuery({
		queryKey,
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<NotificationPreferences>>(
				"/api/v1/notification-preferences/me",
				{ signal },
			)
			return data
		},
	})
}

export const useUpdateNotificationPreferences = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<
		NotificationPreferences,
		ErrorResponse,
		UpdateNotificationPreferencesPayload
	>({
		mutationKey: [queryKeys.updateNotificationPreferences],
		mutationFn: async (payload) => {
			const {
				data: { data },
			} = await httpClient.put<SuccessResponse<NotificationPreferences>>(
				"/api/v1/notification-preferences/me",
				payload,
			)
			return data
		},
		onSuccess: (data) => {
			queryClient.setQueryData([queryKeys.notificationPreferences], data)
		},
	})
}
